import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { startTour, hasSeenTour } from "@/lib/appTour";

/**
 * Handles the return leg of the Emergent Google Auth flow. When the user is
 * redirected back to `/auth/callback#session_id=xxx`, we exchange that
 * session id server-side, receive our normal Brimi JWT, and enter the app.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { applyGoogleSession } = useAuth();
  // useRef (not state) — guards against StrictMode double-invocation.
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      toast.error("Missing Google session — please try again.");
      navigate("/login", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", {
          session_id: sessionId,
        });
        applyGoogleSession(data);
        toast.success("Signed in with Google 👋");
        // Clear the fragment so refresh doesn't retrigger this
        window.history.replaceState(null, "", "/subjects");
        navigate("/subjects", { replace: true });
        // First-time users get the walkthrough.
        if (!hasSeenTour()) {
          setTimeout(() => startTour({ navigate }), 800);
        }
      } catch (e) {
        toast.error(
          e?.response?.data?.detail || "Google sign-in failed — please try again.",
        );
        navigate("/login", { replace: true });
      }
    })();
  }, [applyGoogleSession, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[hsl(var(--brimi-cream))] gap-4">
      <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--brimi-blue))] text-white flex items-center justify-center">
        <Sparkles className="w-7 h-7" />
      </div>
      <Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--brimi-blue))]" />
      <p className="text-sm font-bold text-muted-foreground">
        Signing you in with Google…
      </p>
    </div>
  );
}
